import { FC, useMemo, useState } from "react";
import { FormikHelpers } from "formik";
import cn from "classnames";
import {
  Button,
  Form,
  FormField,
  MessageField,
  notification,
  useAutocomplete,
  useFormValues,
  useSubmit,
} from "@epcnetwork/core-ui-kit";

import { ListModel } from "models";
import { useUser } from "hooks";
import { PostSuppressionFormData } from "api/suppression/suppression.interfaces";
import { getLists, getSuppression, putSuppression } from "api";
import {
  suppressionCategoryOptions,
  suppressionEspOptions,
  suppressionStatusOptions,
} from "../list/suppression-list.constants";
import { SuppressionEditFormProps, UpdateSuppressionValues } from "./suppression-form.types";
import {
  updateSuppressionInitialValues,
  suppressionTypes,
  updateValidationSchema,
  mapSuppressionListOptions,
} from "./suppression-form.constants";
import { SuppressPopup } from "./suppress-popup/suppress-popup";

import styles from "./suppression-form.module.css";

export const SuppressionEditForm: FC<SuppressionEditFormProps> = ({
  closeModal,
  handleItemChange,
  editId,
}) => {
  const { isAdmin } = useUser();

  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const [selectedLists, setSelectedLists] = useState<ListModel[]>([]);

  const handleClosePopup = () => {
    setError("");
    setIsOpen(false);
  };

  const { formProps, mapInitialValues } = useFormValues(
    updateSuppressionInitialValues,
    getSuppression.setParams({ suppressionId: editId }),
    true,
  );

  mapInitialValues((payload) => {
    setValue(payload.value);
    setSelectedLists(payload.lists);

    return {
      type: payload.type,
      value: payload.value,
      esps: payload.esps,
      categories: payload.categories,
      status: payload.status,
      tags: payload.tags,
      listIds: payload.lists.map(({ id }) => id),
    };
  });

  const { onSubmit, onSubmitSuccess, onSubmitError } = useSubmit(
    putSuppression.setParams({ id: editId }),
  );
  onSubmitSuccess((payload) => {
    handleItemChange(payload);
    const title = "Suppression edited";
    const subtitle = "You have successfully edited suppression.";

    notification.success(title, subtitle);
  });
  onSubmitError((error) => setError(error.message));

  const handleSubmit = async (
    payload: UpdateSuppressionValues,
    helpers: FormikHelpers<UpdateSuppressionValues>,
  ) => {
    const { value, type, ...data } = payload;
    // @ts-ignore
    await onSubmit(data as PostSuppressionFormData, helpers);
  };

  const listTypeAutocomplete = useAutocomplete(getLists, "id", "name");

  const { isOptionSelected, isFirstListSpecial } = useMemo(() => {
    const isOptionSelected = selectedLists.length > 0;
    const first = selectedLists[0];
    const isFirstListSpecial = isOptionSelected && Boolean(first?.isGlobal || first?.isHardBounce);

    return { isOptionSelected, isFirstListSpecial };
  }, [selectedLists]);

  const mapListOptions = (
    options: (typeof listTypeAutocomplete)["fetchOptions"],
  ): (typeof listTypeAutocomplete)["fetchOptions"] =>
    mapSuppressionListOptions(options, { isAdmin, selectedLists });

  const getListsLabel = () => {
    if (!isOptionSelected) return "Lists";
    if (isFirstListSpecial) return "Lists (special)";
    return "Lists (local)";
  };

  return (
    <div className={styles.container}>
      <Form
        {...formProps}
        onSubmit={handleSubmit}
        validationSchema={updateValidationSchema}
        enableReinitialize
      >
        {({ isValid }) => {
          return (
            <>
              <div className={styles.title}>Edit</div>
              <MessageField message={error} className={styles.errorMsg} />
              <div className={styles.row}>
                <FormField
                  type="select"
                  name="type"
                  label="Type"
                  placeholder="Choose type from the list"
                  options={suppressionTypes}
                  disabled
                  disableClearing
                  disableArrow
                  required
                />
                <FormField
                  type="select"
                  name="categories"
                  label="Categories"
                  placeholder="Choose Category(ies) from the list"
                  options={suppressionCategoryOptions}
                  className={styles.suppressionLabel}
                  isMulti
                  multilineChips
                  maxChipsVisible={3}
                />
                <FormField
                  type="text"
                  name="value"
                  label="Value"
                  placeholder="Enter Email"
                  disabled
                  onChange={(value) => setValue(value)}
                  className={styles.suppressionLabel}
                  required
                />
                <FormField
                  type="select"
                  name="esps"
                  label="Esp"
                  placeholder="Choose ESP(s) from the list"
                  options={suppressionEspOptions}
                  className={styles.suppressionLabel}
                  isMulti
                  multilineChips
                  maxChipsVisible={3}
                />
                <FormField
                  type="select"
                  name="status"
                  label="Email list verification service status"
                  placeholder="Choose ELVSS from the list"
                  options={suppressionStatusOptions}
                  className={styles.suppressionLabel}
                />
                <FormField
                  type="select"
                  name="listIds"
                  label={getListsLabel()}
                  placeholder="Select from the list"
                  asyncOptions={{
                    ...listTypeAutocomplete,
                    fetchOptions: mapListOptions(listTypeAutocomplete.fetchOptions),
                  }}
                  className={styles.suppressionLabel}
                  multilineChips
                  onChange={setSelectedLists}
                  maxChipsVisible={2}
                  isMulti
                  isSearchable
                  required
                />
              </div>

              {isOpen && (
                <SuppressPopup
                  isOpen={isOpen}
                  setClose={handleClosePopup}
                  value={value}
                  error={error}
                />
              )}
              <div className={cn(styles.buttons, styles.buttonsWrapper)}>
                <Button appearance="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!isValid}>
                  Submit
                </Button>
              </div>
            </>
          );
        }}
      </Form>
    </div>
  );
};
